import Pagination from 'components/common/Pagination';
import useUsersTable from 'hooks/service/useUsersTable';
import palette from 'lib/styles/palette';
import * as usersAPI from 'lib/api/service/users';
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import InviteModal from './InviteModal';
import { useMutation, useQueryClient } from 'react-query';
import useLoginCheck from 'hooks/common/useLoginCheck';
import AskModal from 'components/common/AskModal';

const UserRow = ({ user, userType }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalText, setModalText] = useState('');

  const queryClient = useQueryClient();

  const storeId = Number(user.storeId);
  const userId = Number(user.userId);

  const permitUserMutation = useMutation(usersAPI.permitUser, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      setShowModal(false);
    },
  });
  useLoginCheck(permitUserMutation.error?.response, () => {
    permitUserMutation.mutate({
      storeId,
      userId,
      type: permitUserMutation.variables?.type,
    });
  });

  const deleteInviteMutation = useMutation(usersAPI.deleteInvite, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      setShowModal(false);
    },
  });
  useLoginCheck(deleteInviteMutation.error?.response, () => {
    deleteInviteMutation.mutate({
      email: user.email,
      storeId: String(storeId),
    });
  });

  const onPermit = event => {
    setShowModal(true);
    setModalText(event.target.innerHTML);
  };

  return (
    <>
      {showModal && (
        <AskModal
          closeModal={() => setShowModal(false)}
          action={modalText}
          message={
            modalText === '내보내기'
              ? '정말 내보내시겠습니까?'
              : `정말 ${modalText} 하시겠습니까?`
          }
          actionFc={() => {
            if (modalText === '삭제') {
              deleteInviteMutation.mutate({
                email: user.email,
                storeId: String(storeId),
              });
            } else {
              permitUserMutation.mutate({
                storeId,
                userId,
                type: modalText === '승인' ? 1 : 0,
              });
            }
          }}
        />
      )}
      <div className="row">
        <div className="cell small">{user.code ? user.code : '미입력'}</div>
        <div className="cell">{user.store}</div>
        {userType === 0 && <div className="cell small">{user.level}</div>}
        <div className="cell">{user.user}</div>
        <div className="cell" title={user.email}>
          {user.email}
        </div>
        <div className="cell small">
          {userType === 0 ? (
            user.type === 1 ? (
              <div className="btn-wrapper">
                <button
                  className="btn"
                  style={{ width: 50 }}
                  onClick={event => onPermit(event)}
                >
                  거절
                </button>
                <button
                  className="btn"
                  style={{ width: 50 }}
                  onClick={event => onPermit(event)}
                >
                  승인
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div className="status-text">초대 완료</div>
                <div
                  className="user-low-delete-button"
                  onClick={event => onPermit(event)}
                >
                  삭제
                </div>
              </div>
            )
          ) : (
            <button className="btn" onClick={event => onPermit(event)}>
              내보내기
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const UsersTable = ({
  title,
  userType,
  search,
  currentPage,
  setCurrentPage,
  usersLoading,
}) => {
  const [showModal, setShowModal] = useState(false);

  const { data } = useUsersTable(userType, search, currentPage, setCurrentPage);
  const res = data?.data;
  const totalPage = Math.ceil(res?.count / 5);

  const closeModal = useCallback(() => setShowModal(false), []);

  if (usersLoading.length !== 0) return null;

  return (
    <>
      {showModal && (
        <InviteModal
          title={title}
          userType={userType}
          closeModal={closeModal}
        />
      )}
      <UsersTableBlock>
        <div className="header">
          <div className="title">{title}</div>
          {title.includes('관리자') && (
            <button className="btn" onClick={() => setShowModal(true)}>
              초대하기
            </button>
          )}
        </div>
        <div className="box">
          <div className="row bold">
            <div className="cell small">CU 코드</div>
            <div className="cell">소속명</div>
            {userType === 0 && <div className="cell small">직급</div>}
            <div className="cell">이름</div>
            <div className="cell">이메일</div>
            <div className="cell small" />
          </div>
          {res?.rows?.length === 0 ? (
            <div className="no-result">목록이 없습니다</div>
          ) : (
            <>
              {res?.rows?.map((user, i) => (
                <UserRow key={i} user={user} userType={userType} />
              ))}
              <Pagination
                totalPage={totalPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </>
          )}
        </div>
      </UsersTableBlock>
    </>
  );
};

export default UsersTable;

const UsersTableBlock = styled.div`
  .header {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    button {
      margin-left: 16px;
    }
  }

  .box {
    margin-top: 1rem;
    padding: 1rem 2rem;
    display: flex;
    flex-direction: column;
  }

  .status-text {
    color: ${palette.theme[3]};
    font-size: 12px;
    font-weight: bold;
  }

  .no-result {
    padding: 2rem;
    text-align: center;
    font-weight: 500;
  }

  .btn-wrapper {
    button {
      font-size: 8px;
      padding: 4px 12px;
    }
    button + button {
      margin-left: 4px;
    }
  }

  .user-low-delete-button {
    width: 50px;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: 10px;
    background-color: ${palette.theme[3]};
    color: #fff;
    font-size: 8px;
    border-radius: 6px;
    cursor: pointer;
  }
`;
